/**
 * CanvasToolbar — bottom-right zoom + fit controls.
 *
 * Phase 4.4 visual: cream icon-buttons with hairline border, lucide icons,
 * mirrors the prototype's `.canvas__controls` placement. Legend has been
 * split into its own component (CanvasLegend) per the prototype, which
 * places it top-right.
 */

import { LayoutDashboard, Maximize2, Minus, Plus } from 'lucide-react';
import { useReactFlow } from '@xyflow/react';
import { cn } from '@/lib/utils';

function IconBtn({
  onClick,
  label,
  children,
  disabled = false,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'inline-grid h-[30px] w-[30px] place-items-center rounded-lg transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
      style={{
        background: 'hsl(var(--card))',
        border: '1px solid hsl(var(--border))',
        color: 'hsl(var(--muted-foreground))',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      {children}
    </button>
  );
}

interface CanvasToolbarProps {
  /** Re-runs dagre layout on the current node + edge set. */
  onRelayout?: () => void;
}

export function CanvasToolbar({ onRelayout }: CanvasToolbarProps) {
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  return (
    <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-1.5">
      <IconBtn label="Zoom in" onClick={() => void zoomIn({ duration: 200 })}>
        <Plus className="h-3.5 w-3.5" />
      </IconBtn>
      <IconBtn label="Zoom out" onClick={() => void zoomOut({ duration: 200 })}>
        <Minus className="h-3.5 w-3.5" />
      </IconBtn>
      <IconBtn
        label="Fit to screen"
        onClick={() => void fitView({ duration: 300, padding: 0.2 })}
      >
        <Maximize2 className="h-3.5 w-3.5" />
      </IconBtn>
      {onRelayout && (
        <IconBtn label="Re-arrange canvas" onClick={onRelayout}>
          <LayoutDashboard className="h-3.5 w-3.5" />
        </IconBtn>
      )}
    </div>
  );
}
